/**
 * Writers that need no compression library: binary little-endian PLY and the
 * 32-byte-per-splat antimatter15 `.splat` format. Output is produced as a list
 * of chunks so very large scenes never need one contiguous buffer.
 * Compressed PLY, SOG, SPZ and LOD sets go through splat-transform in the
 * editor's worker.
 */
import type { BakedSplats } from './bake';
import { plyColumnOrder } from './bake';
import { SH_C0 } from './SplatEditModel';

const ROWS_PER_CHUNK = 65536;

export function plyHeader(numSplats: number, names: string[], comments: string[] = []): string {
    return [
        'ply',
        'format binary_little_endian 1.0',
        ...comments.map((c) => `comment ${c}`),
        `element vertex ${numSplats}`,
        ...names.map((nm) => `property float ${nm}`),
        'end_header',
        '',
    ].join('\n');
}

/** Encode baked splats as binary PLY chunks (header first). */
export function encodePly(baked: BakedSplats, comments: string[] = ['Generated by StorySplat splat editor']): Uint8Array[] {
    const names = plyColumnOrder(baked.shBands);
    const cols = names.map((nm) => {
        const c = baked.columns.get(nm);
        if (!c) throw new Error(`encodePly: missing column ${nm}`);
        return c;
    });
    const parts: Uint8Array[] = [new TextEncoder().encode(plyHeader(baked.numSplats, names, comments))];
    const stride = names.length;
    for (let start = 0; start < baked.numSplats; start += ROWS_PER_CHUNK) {
        const end = Math.min(baked.numSplats, start + ROWS_PER_CHUNK);
        const rows = end - start;
        const buf = new Float32Array(rows * stride);
        for (let r = 0; r < rows; r++) {
            const i = start + r;
            const o = r * stride;
            for (let c = 0; c < stride; c++) buf[o + c] = cols[c][i];
        }
        // Float32Array is little-endian on every platform browsers ship on
        parts.push(new Uint8Array(buf.buffer));
    }
    return parts;
}

/** Encode baked splats in the antimatter15 `.splat` layout (no SH). */
export function encodeSplat(baked: BakedSplats): Uint8Array[] {
    const c = (nm: string) => baked.columns.get(nm)!;
    const x = c('x'), y = c('y'), z = c('z');
    const s0 = c('scale_0'), s1 = c('scale_1'), s2 = c('scale_2');
    const r0 = c('rot_0'), r1 = c('rot_1'), r2 = c('rot_2'), r3 = c('rot_3');
    const d0 = c('f_dc_0'), d1 = c('f_dc_1'), d2 = c('f_dc_2');
    const op = c('opacity');
    const parts: Uint8Array[] = [];
    const clampByte = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
    for (let start = 0; start < baked.numSplats; start += ROWS_PER_CHUNK) {
        const end = Math.min(baked.numSplats, start + ROWS_PER_CHUNK);
        const rows = end - start;
        const buf = new ArrayBuffer(rows * 32);
        const f = new Float32Array(buf);
        const u = new Uint8Array(buf);
        for (let r = 0; r < rows; r++) {
            const i = start + r;
            const fo = r * 8;
            f[fo] = x[i]; f[fo + 1] = y[i]; f[fo + 2] = z[i];
            f[fo + 3] = Math.exp(s0[i]); f[fo + 4] = Math.exp(s1[i]); f[fo + 5] = Math.exp(s2[i]);
            const bo = r * 32 + 24;
            u[bo] = clampByte((0.5 + SH_C0 * d0[i]) * 255);
            u[bo + 1] = clampByte((0.5 + SH_C0 * d1[i]) * 255);
            u[bo + 2] = clampByte((0.5 + SH_C0 * d2[i]) * 255);
            u[bo + 3] = clampByte((1 / (1 + Math.exp(-op[i]))) * 255);
            const ql = Math.hypot(r0[i], r1[i], r2[i], r3[i]) || 1;
            u[bo + 4] = clampByte((r0[i] / ql) * 128 + 128);
            u[bo + 5] = clampByte((r1[i] / ql) * 128 + 128);
            u[bo + 6] = clampByte((r2[i] / ql) * 128 + 128);
            u[bo + 7] = clampByte((r3[i] / ql) * 128 + 128);
        }
        parts.push(u);
    }
    return parts;
}

/** Concatenate chunks (tests / small exports only). */
export function concatChunks(parts: Uint8Array[]): Uint8Array {
    const total = parts.reduce((s, p) => s + p.byteLength, 0);
    const out = new Uint8Array(total);
    let o = 0;
    for (const p of parts) { out.set(p, o); o += p.byteLength; }
    return out;
}

/** Minimal binary PLY reader for float/uchar vertex properties (tests and round-trip checks). */
export function parsePly(bytes: Uint8Array): { numSplats: number; columns: Map<string, Float32Array> } {
    const headerEnd = findHeaderEnd(bytes);
    const header = new TextDecoder().decode(bytes.subarray(0, headerEnd));
    const lines = header.split('\n');
    let count = 0;
    const props: { name: string; type: string }[] = [];
    let inVertex = false;
    for (const line of lines) {
        const t = line.trim().split(/\s+/);
        if (t[0] === 'element') { inVertex = t[1] === 'vertex'; if (inVertex) count = parseInt(t[2], 10); }
        else if (t[0] === 'property' && inVertex) props.push({ type: t[1], name: t[2] });
    }
    const sizes: Record<string, number> = { float: 4, float32: 4, uchar: 1, uint8: 1, double: 8, int: 4, uint: 4 };
    const stride = props.reduce((s, p) => s + (sizes[p.type] ?? 4), 0);
    const view = new DataView(bytes.buffer, bytes.byteOffset + headerEnd, count * stride);
    const columns = new Map<string, Float32Array>();
    props.forEach((p) => columns.set(p.name, new Float32Array(count)));
    for (let i = 0; i < count; i++) {
        let o = i * stride;
        for (const p of props) {
            const col = columns.get(p.name)!;
            switch (p.type) {
                case 'uchar': case 'uint8': col[i] = view.getUint8(o); o += 1; break;
                case 'double': col[i] = view.getFloat64(o, true); o += 8; break;
                case 'int': col[i] = view.getInt32(o, true); o += 4; break;
                case 'uint': col[i] = view.getUint32(o, true); o += 4; break;
                default: col[i] = view.getFloat32(o, true); o += 4;
            }
        }
    }
    return { numSplats: count, columns };
}

function findHeaderEnd(bytes: Uint8Array): number {
    const marker = 'end_header\n';
    const limit = Math.min(bytes.length, 1 << 16);
    const text = new TextDecoder().decode(bytes.subarray(0, limit));
    const idx = text.indexOf(marker);
    if (idx < 0) throw new Error('parsePly: no end_header');
    return new TextEncoder().encode(text.slice(0, idx + marker.length)).length;
}
